<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { PreviewIncidentReportResponse } from '@roadfootage/api-client'

const props = defineProps<{
  incident: PreviewIncidentReportResponse
}>()

const mapContainer = ref<HTMLElement | null>(null)
let mapInstance: L.Map | null = null

function initMap() {
  if (!mapContainer.value || !props.incident.location?.coordinates) return

  // GeoJSON Point coordinates are [longitude, latitude]
  const [lng, lat] = props.incident.location.coordinates

  const satelliteLayer = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 20
    }
  )

  const streetLayer = new L.TileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      maxZoom: 20
    }
  )

  mapInstance = new L.Map(mapContainer.value, {
    center: [lat, lng],
    zoom: 16,
    layers: [satelliteLayer],
    // Read-only — disable all interaction
    dragging: false,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    boxZoom: false,
    keyboard: false,
    touchZoom: false,
    zoomControl: false
  })

  new L.Control.Layers({ 'Street Map': streetLayer, Satellite: satelliteLayer }, undefined, {
    position: 'topright'
  }).addTo(mapInstance)

  // Decorative: the address beside the map names the place, and the map itself is not interactive (RF-120).
  new L.Marker([lat, lng], {
    keyboard: false,
    interactive: false,
    icon: new L.DivIcon({
      html: `<i class="q-icon mdi mdi-map-marker" style="font-size:32px;color:#e74c3c;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))"></i>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      className: ''
    })
  }).addTo(mapInstance)
}

onMounted(() => {
  initMap()
})

onBeforeUnmount(() => {
  mapInstance?.remove()
  mapInstance = null
})
</script>

<template>
  <!-- full-height + column: stretches to match the media player card height on desktop -->
  <q-card class="full-height column">
    <!-- Map renders here when location is available -->
    <template v-if="incident.location?.coordinates">
      <!-- col class = flex: 1; makes the map fill remaining card height on desktop -->
      <div ref="mapContainer" class="map-container col" />
      <q-separator />
      <q-card-section class="q-py-sm">
        <div class="row items-start no-wrap text-caption text-grey-8">
          <q-icon name="mdi-map-marker" class="q-mr-xs q-mt-xs" color="primary" />
          <span>{{ incident.address?.formattedAddress ?? 'Address unavailable' }}</span>
        </div>
      </q-card-section>
    </template>

    <!-- Fallback when no location data -->
    <template v-else>
      <q-card-section class="flex flex-center column q-py-xl text-grey-5 q-gutter-y-sm">
        <q-icon name="mdi-map-marker-off" size="xl" />
        <div class="text-caption">Location unavailable</div>
      </q-card-section>
    </template>
  </q-card>
</template>

<style lang="scss" scoped>
.map-container {
  // Fixed height on mobile; on desktop the card stretches to match the media player
  // via full-height + column on q-card, and col (flex: 1) here fills remaining space.
  min-height: 250px;
  width: 100%;
}
</style>
